import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { pmGet, pmPost } from '../api';
import './MeetingDecks.css';
import { HelpPanel, type HelpContent } from '../../portfolio_definition/HelpPanel';
import { withFinalizedRule } from '../finalizedRuleHelp';

/**
 * Meeting Decks — the screen behind BOTH Weekly Status Meetings and SteerCo Deck Creation.
 *
 * Cloned from `ai_readiness_template/views/roadmap-execution/meeting-decks.ejs`.
 *
 * ── WHY THIS REPLACES TWO "COMING SOON" STUBS ──────────────────────────────────────────
 *
 * `views/roadmap-execution/weekly-status.ejs` and `steerco-deck.ejs` ARE twelve-line "Coming
 * soon" stubs — and nothing routes to them. Both menu entries are served by
 * `_renderMeetingDecks(req, res, slug)` (`routes/roadmap-execution.js:549-566`), which renders
 * THIS view with a different `deckSlug`. The stubs are dead files.
 *
 * An earlier pass here cloned the stubs, which is why both screens said "being built out" when
 * the source has had them working all along. Reading the route rather than the file name is
 * what settles which view a menu entry actually uses.
 *
 * ── ONE COMPONENT, TWO SCREENS — and that IS the source's shape ────────────────────────
 *
 * §1.2.1 forbids collapsing several source screens into one parameterised component. This is
 * not that: the source has ONE view parameterised by `deckSlug`, and the two routes differ
 * only in which slug they pass. Splitting it in two would be the deviation.
 */

type Dict = Record<string, unknown>;

export type DeckSlug = 'weekly-status' | 'steerco-deck';

interface Deck {
  id: number;
  title: string | null;
  meeting_date: string | null;
  delivery_project_id: string | null;
  source: string | null;
  file_name: string | null;
  slide_count: number | null;
  bytes: number | null;
  created: string | null;
}
interface Template {
  id: number;
  name: string;
  kind: string | null;
  slide_count: number | null;
}
interface Project {
  delivery_project_id: string;
  description: string | null;
  methodology: string | null;
}

const MON = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const todayISO = () => new Date().toISOString().slice(0, 10);

/**
 * "How this works" — cloned from `ai_readiness_template/views/roadmap-execution/meeting-decks.ejs`,
 * its `mdHelp` object. Copy, colours and section order are the source's;
 * only the link hrefs are rewritten to this platform's routes, and the entities in
 * fields HelpPanel renders as TEXT (title, section label, term, link label) are
 * decoded, because only the HTML fields go through `dangerouslySetInnerHTML`.
 *
 * A FUNCTION, not a constant, because the source's is not a constant either: one view
 * serves both Weekly Status Meetings and SteerCo Deck Creation, and its help reads
 * `deckLabel` and `deckSlug` from the view's scope for its title, icon, purpose and
 * "where it fits". Here those arrive as arguments instead.
 */
const mdHelp = (deckLabel: string, deckSlug: DeckSlug): HelpContent => ({
    title: deckLabel,
    icon: (deckSlug === 'weekly-status' ? '🗓️' : '📊'),
    purpose: "Create and track the <strong>" + deckLabel + "</strong> decks for a project. On the left, <strong>Generate a deck with AI</strong> from the project's live <strong>Execution Control Center</strong> status, or <strong>Upload a deck</strong> — and browse meeting dates on the calendar. Generated and uploaded files appear on the right.",
    doHere: [
      "<strong>Generate with AI</strong>: pick a project, a saved template and a meeting date — the deck content is built from the project's deliverable progress, risks, events and (for Agile) backlog &amp; sprints.",
      "<strong>Several projects in one deck</strong>: <strong>Ctrl&#8209;click</strong> (&#8984;&#8209;click on a Mac) to select more than one project. You get a <strong>single deck</strong> in which each project keeps its usual structure and the projects simply follow one another — nothing is merged or averaged across them. Each project is written up on its own data, and page numbers run through the whole deck. Building on a previous deck applies to a single project only, so it switches off.",
      "<strong>Upload a deck</strong> you've prepared outside the app.",
      "Use the <strong>calendar</strong> to see decks by meeting date (click a date to filter), then <strong>download</strong> or <strong>delete</strong> any deck on the right.",
      "The list on the right is <strong>empty until you pick a project</strong> &mdash; it then shows the decks generated for that project, and nothing from any other. A deck built from several projects appears under each of them.",
      "Tick <strong>Active initiatives only</strong> to leave out work that has not begun. An initiative is <strong>active</strong> once any of its <strong>work items or actions has been started</strong>, or it <strong>starts within the next 30 days</strong>. <strong>Completed initiatives are always kept</strong> &mdash; delivered work is part of the picture and counts toward every total on the deck. The deck says on its title slide when it is scoped this way, and how many initiatives were left out."
    ],
    fits: 'Execution Control Center (status) + Report Templates &rarr; <strong>' + deckLabel + '</strong>',
    firstTime: "Ensure the project has data in the <strong>Execution Control Center</strong>, add a template on <strong>Report Templates</strong>, then click <strong>Generate deck</strong>. Try selecting two projects to see them stacked in one deck.",
    links: [
      { label: 'Manage Report Templates →', href: '/project_management/program-management/status-reporting/report-templates' },
      { label: 'Execution Control Center →', href: '/project_management/program-management/delivery-execution/execution-control-center' }
    ]
  });

export function MeetingDecks({ slug }: { slug: DeckSlug }) {
  const [decks, setDecks] = useState<Deck[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [deckLabel, setDeckLabel] = useState('');
  const [templatesEnabled, setTemplatesEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [tab, setTab] = useState<'generate' | 'upload'>('generate');

  /* Generate form */
  const [picked, setPicked] = useState<string[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [baseDeckId, setBaseDeckId] = useState('');
  const [genDate, setGenDate] = useState(todayISO());
  const [genTitle, setGenTitle] = useState('');
  const [generating, setGenerating] = useState(false);
  const [projError, setProjError] = useState(false);

  /* Upload form */
  const [upProject, setUpProject] = useState('');
  const [upFile, setUpFile] = useState<File | null>(null);
  const [upDate, setUpDate] = useState(todayISO());
  const [upTitle, setUpTitle] = useState('');
  const [uploading, setUploading] = useState(false);

  /* Calendar */
  const now = new Date();
  const [calY, setCalY] = useState(now.getFullYear());
  const [calM, setCalM] = useState(now.getMonth());
  const [filterDate, setFilterDate] = useState<string | null>(null);

  const selWrap = useRef<HTMLDivElement | null>(null);

  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const [rootH, setRootH] = useState<number | null>(null);
  useEffect(() => {
    if (!rootEl) return;
    const main = rootEl.closest('main');
    const measure = () => {
      const top = rootEl.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const next = Math.max(320, Math.floor(window.innerHeight - top - inset));
      setRootH((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [rootEl]);

  const load = useCallback(() => {
    setLoading(true);
    pmGet<{
      success: boolean;
      deckLabel: string;
      decks: Deck[];
      templates: Template[];
      projects: Project[];
      templatesEnabled: boolean;
      error?: string;
    }>(`/meeting-decks/data?deck_type=${encodeURIComponent(slug)}`)
      .then((d) => {
        if (!d.success) throw new Error(d.error ?? 'failed');
        setDecks(d.decks ?? []);
        setTemplates(d.templates ?? []);
        setProjects(d.projects ?? []);
        setDeckLabel(d.deckLabel);
        setTemplatesEnabled(d.templatesEnabled);
        setError(null);
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    load();
    /* Switching deck type resets the form — a SteerCo deck's selection is not a weekly one's. */
    setPicked([]);
    setBaseDeckId('');
    setFilterDate(null);
  }, [load]);

  /* Close the pulldown on an outside click or Escape, as the source's document handlers do. */
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (selWrap.current && !selWrap.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  /*
   * "Build on a previous deck" carries ONE programme's story forward, so it cannot apply to a
   * combined deck — it is DISABLED for a multi-selection rather than left looking available
   * and then ignored.
   */
  const multi = picked.length > 1;
  const baseDecks = useMemo(() => {
    if (picked.length !== 1) return [];
    return decks
      .filter((d) => d.delivery_project_id === picked[0])
      .sort(
        (a, b) =>
          String(b.meeting_date ?? '').localeCompare(String(a.meeting_date ?? '')) || b.id - a.id,
      );
  }, [decks, picked]);

  const togglePick = (id: string, additive: boolean) =>
    setPicked((prev) => {
      setProjError(false);
      if (!additive) return prev.length === 1 && prev[0] === id ? [] : [id];
      return prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
    });

  const pickedLabel =
    picked.length === 0
      ? '— Select a program —'
      : picked.length === 1
        ? (() => {
            const p = projects.find((x) => x.delivery_project_id === picked[0]);
            return p
              ? `${p.delivery_project_id}${p.description ? ` — ${p.description}` : ''}`
              : picked[0]!;
          })()
        : `${picked.length} programs selected`;

  const generate = async () => {
    /* The source guards here too: the pulldown posts hidden inputs, so an empty selection would
       otherwise reach the server as a bare 400. */
    if (!picked.length) {
      setProjError(true);
      setMenuOpen(true);
      return;
    }
    setGenerating(true);
    setNotice(null);
    try {
      const d = await pmPost<{ success: boolean; error?: string }>('/meeting-decks/generate', {
        deck_type: slug,
        delivery_project_id: picked,
        template_id: templateId || null,
        base_deck_id: baseDeckId || null,
        meeting_date: genDate || null,
        title: genTitle || null,
      } as Dict);
      if (!d.success) throw new Error(d.error ?? 'Generation failed');
      setNotice('Deck generated.');
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setGenerating(false);
    }
  };

  const upload = async () => {
    if (!upFile) return;
    setUploading(true);
    setNotice(null);
    try {
      const fd = new FormData();
      fd.append('deck_type', slug);
      if (upProject) fd.append('delivery_project_id', upProject);
      fd.append('file', upFile);
      if (upDate) fd.append('meeting_date', upDate);
      if (upTitle) fd.append('title', upTitle);
      const res = await fetch('/api/project_management/meeting-decks/upload', {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });
      const d = (await res.json()) as { success: boolean; error?: string };
      if (!d.success) throw new Error(d.error ?? 'Upload failed');
      setNotice('Deck uploaded.');
      setUpFile(null);
      setUpTitle('');
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const removeDeck = async (id: number) => {
    if (!window.confirm('Delete this deck?')) return;
    try {
      const d = await pmPost<{ success: boolean; error?: string }>(
        `/meeting-deck/${id}/delete`,
        {},
      );
      if (!d.success) throw new Error(d.error ?? 'Delete failed');
      load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  /* Calendar: a dot per date that has decks, and clicking one filters the list. */
  const counts = useMemo(() => {
    const m: Record<string, number> = {};
    decks.forEach((d) => {
      if (d.meeting_date) m[d.meeting_date] = (m[d.meeting_date] ?? 0) + 1;
    });
    return m;
  }, [decks]);

  const calDays = useMemo(() => {
    const first = new Date(calY, calM, 1).getDay();
    const days = new Date(calY, calM + 1, 0).getDate();
    const cells: ({ d: number; ds: string } | null)[] = [];
    for (let i = 0; i < first; i++) cells.push(null);
    for (let d = 1; d <= days; d++) {
      cells.push({
        d,
        ds: `${calY}-${String(calM + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`,
      });
    }
    return cells;
  }, [calY, calM]);

  const calMove = (n: number) => {
    let m = calM + n;
    let y = calY;
    if (m < 0) {
      m = 11;
      y--;
    }
    if (m > 11) {
      m = 0;
      y++;
    }
    setCalM(m);
    setCalY(y);
  };

  const shown = filterDate ? decks.filter((d) => d.meeting_date === filterDate) : decks;

  if (loading && !deckLabel) return <div style={{ padding: 24, color: '#90a4ae' }}>Loading…</div>;

  return (
    <div className="md-page" ref={setRootEl} style={rootH ? { height: rootH } : undefined}>
      <HelpPanel help={withFinalizedRule(mdHelp(deckLabel, slug))} />
      <div className="md-head">
        <h1 className="page-header__title">{deckLabel}</h1>
        <p>
          Generate a deck with AI from the project&rsquo;s <strong>Execution Control Center</strong>{' '}
          status (using a saved template), or upload one. Decks are stored in the database and
          tracked by meeting date.
        </p>
      </div>

      <div className="md-body">
        {error ? (
          <div style={{ color: '#c62828', fontSize: 13, marginBottom: 12 }}>{error}</div>
        ) : null}
        {notice ? (
          <div style={{ color: '#2e7d32', fontSize: 13, marginBottom: 12 }}>{notice}</div>
        ) : null}

        <div className="md-cols">
          <div className="md-left">
            <div className="md-card">
              <div className="md-card-content">
                <div className="md-tabs">
                  <button
                    type="button"
                    className={`mdtab${tab === 'generate' ? ' active' : ''}`}
                    onClick={() => setTab('generate')}
                  >
                    <i className="material-icons">auto_awesome</i> Generate with AI
                  </button>
                  <button
                    type="button"
                    className={`mdtab${tab === 'upload' ? ' active' : ''}`}
                    onClick={() => setTab('upload')}
                  >
                    <i className="material-icons">upload_file</i> Upload a deck
                  </button>
                </div>

                {tab === 'generate' ? (
                  <div>
                    <label className="md-label">
                      Project <span className="hint">(Ctrl-click, or ⌘-click on a Mac, to pick several)</span>
                    </label>
                    <div className="mdsel" ref={selWrap}>
                      <button
                        type="button"
                        className="mdsel-btn"
                        aria-haspopup="listbox"
                        aria-expanded={menuOpen}
                        onClick={() => setMenuOpen((o) => !o)}
                      >
                        <span className={`mdsel-label${picked.length ? '' : ' placeholder'}`}>
                          {pickedLabel}
                        </span>
                        <i className="material-icons mdsel-caret">arrow_drop_down</i>
                      </button>
                      {menuOpen ? (
                        <div className="mdsel-menu" role="listbox" aria-multiselectable="true">
                          {/* Select all / Clear — the Ctrl-click hint covers picking a few, but
                              there was no way to take every project in one action. */}
                          {projects.length > 1 ? (
                            <div className="mdsel-opt mdsel-opt--all">
                              <button
                                type="button"
                                className="mdsel-allbtn"
                                onClick={() => setPicked(projects.map((p) => p.delivery_project_id))}
                              >
                                Select all
                              </button>
                              <button type="button" className="mdsel-allbtn" onClick={() => setPicked([])}>
                                Clear
                              </button>
                            </div>
                          ) : null}
                          {projects.map((p) => (
                            <div
                              className="mdsel-opt"
                              role="option"
                              tabIndex={0}
                              aria-selected={picked.includes(p.delivery_project_id)}
                              key={p.delivery_project_id}
                              onClick={(e) =>
                                togglePick(p.delivery_project_id, e.ctrlKey || e.metaKey)
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  togglePick(p.delivery_project_id, e.ctrlKey || e.metaKey);
                                }
                              }}
                            >
                              <i className="material-icons mdsel-tick">check</i>
                              <span>
                                {p.delivery_project_id}
                                {p.description ? ` — ${p.description}` : ''}
                                {p.methodology ? ` (${p.methodology})` : ''}
                              </span>
                            </div>
                          ))}
                          {!projects.length ? (
                            <div className="mdsel-opt" style={{ color: '#90a4ae' }}>
                              No programs available.
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    <p className="md-hint" style={projError ? { color: '#c62828' } : undefined}>
                      {projError
                        ? 'Pick at least one program first.'
                        : multi
                          ? `${picked.length} programs selected — one deck, each program's section one after the other. Building on a previous deck applies to a single program only.`
                          : 'Select one program, or Ctrl-click several to get a single deck with one program after another.'}
                    </p>

                    <label className="md-label">
                      Template{' '}
                      <span className="hint">
                        (optional — compare template-filled vs built-from-data)
                      </span>
                      {!templatesEnabled ? <span className="md-soon"> COMING SOON</span> : null}
                    </label>
                    <select
                      className="md-select"
                      value={templateId}
                      disabled={!templatesEnabled}
                      onChange={(e) => {
                        setTemplateId(e.target.value);
                        if (e.target.value) setBaseDeckId('');
                      }}
                    >
                      <option value="">
                        — None: build full deck from live program data (recommended) —
                      </option>
                      {templatesEnabled
                        ? templates.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                              {t.slide_count ? ` (${t.slide_count} slides)` : ''}
                            </option>
                          ))
                        : null}
                    </select>
                    <p className="md-hint">
                      {!templatesEnabled ? (
                        <>
                          ⏳ Template-based decks are <strong>coming soon</strong> in this
                          environment — decks are built from live program data.
                        </>
                      ) : templateId ? (
                        <>
                          Template selected → <b>your template&rsquo;s</b> existing slides are
                          filled with grounded content (single-project fill). The multi-slide
                          build-out and &ldquo;build on a previous deck&rdquo; apply only to the
                          from-data option.
                        </>
                      ) : (
                        'No template selected → the deck is built as a full multi-slide deck (dashboard, roadmap, budget, per-opportunity) from live program data.'
                      )}
                    </p>

                    <label className="md-label">
                      Build on a previous deck{' '}
                      <span className="hint">
                        (optional — carries the story forward &amp; shows what changed)
                      </span>
                    </label>
                    <select
                      className="md-select"
                      value={baseDeckId}
                      disabled={multi || !!templateId}
                      onChange={(e) => setBaseDeckId(e.target.value)}
                    >
                      <option value="">— None (build fresh from current data) —</option>
                      {baseDecks.map((d) => (
                        <option key={d.id} value={d.id}>
                          {`${d.title ?? `Deck #${d.id}`} · ${d.meeting_date ?? 'no date'} · ${
                            d.source === 'generated' ? 'generated' : 'uploaded'
                          }`}
                        </option>
                      ))}
                    </select>

                    <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                      <div style={{ flex: 1 }}>
                        <label className="md-label">Meeting date</label>
                        <input
                          type="date"
                          className="md-input"
                          value={genDate}
                          onChange={(e) => setGenDate(e.target.value)}
                        />
                      </div>
                      <div style={{ flex: 1.4 }}>
                        <label className="md-label">Title (optional)</label>
                        <input
                          type="text"
                          className="md-input"
                          placeholder="auto"
                          value={genTitle}
                          onChange={(e) => setGenTitle(e.target.value)}
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      className="md-btn"
                      disabled={generating}
                      onClick={() => void generate()}
                    >
                      <i className="material-icons">
                        {generating ? 'hourglass_top' : 'auto_awesome'}
                      </i>
                      {generating ? 'Generating…' : 'Generate deck'}
                    </button>
                    <span style={{ color: '#9e9e9e', fontSize: 11.5, marginLeft: 10 }}>
                      A full deck is built from the program&rsquo;s live data — title, executive
                      summary, health dashboard, roadmap, budget &amp; resource, RAID, and a slide
                      per opportunity.
                    </span>
                  </div>
                ) : (
                  <div>
                    <label className="md-label">Project</label>
                    <select
                      className="md-select pd-project-select"
                      value={upProject}
                      onChange={(e) => setUpProject(e.target.value)}
                    >
                      <option value="">— None —</option>
                      {projects.map((p) => (
                        <option key={p.delivery_project_id} value={p.delivery_project_id}>
                          {p.delivery_project_id} ({p.methodology})
                        </option>
                      ))}
                    </select>
                    <label className="md-label" style={{ marginTop: 10 }}>
                      File (PowerPoint / PDF)
                    </label>
                    <input
                      type="file"
                      accept=".pptx,.ppt,.pdf"
                      className="md-input"
                      onChange={(e) => setUpFile(e.target.files?.[0] ?? null)}
                    />
                    <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                      <div style={{ flex: 1 }}>
                        <label className="md-label">Meeting date</label>
                        <input
                          type="date"
                          className="md-input"
                          value={upDate}
                          onChange={(e) => setUpDate(e.target.value)}
                        />
                      </div>
                      <div style={{ flex: 1.4 }}>
                        <label className="md-label">Title (optional)</label>
                        <input
                          type="text"
                          className="md-input"
                          placeholder="file name"
                          value={upTitle}
                          onChange={(e) => setUpTitle(e.target.value)}
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      className="md-btn"
                      disabled={!upFile || uploading}
                      onClick={() => void upload()}
                    >
                      <i className="material-icons">file_upload</i>
                      {uploading ? 'Uploading…' : 'Upload deck'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="md-card" style={{ marginTop: 20 }}>
              <div className="md-card-content">
                <div className="md-cal-head">
                  <button type="button" className="md-cal-nav" onClick={() => calMove(-1)}>
                    <i className="material-icons">chevron_left</i>
                  </button>
                  <span className="md-cal-label">
                    {MON[calM]} {calY}
                  </span>
                  <button type="button" className="md-cal-nav" onClick={() => calMove(1)}>
                    <i className="material-icons">chevron_right</i>
                  </button>
                </div>
                <div className="md-cal-grid">
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                    <div className="md-cal-dow" key={`${d}${i}`}>
                      {d}
                    </div>
                  ))}
                  {calDays.map((c, i) =>
                    c === null ? (
                      <div key={`e${i}`} />
                    ) : (
                      <div
                        key={c.ds}
                        className={`md-cal-day${counts[c.ds] ? ' has' : ''}${
                          filterDate === c.ds ? ' sel' : ''
                        }`}
                        onClick={() => (counts[c.ds] ? setFilterDate(c.ds) : undefined)}
                      >
                        {c.d}
                        {counts[c.ds] ? (
                          <div className="md-cal-dot">
                            ●{counts[c.ds]! > 1 ? counts[c.ds] : ''}
                          </div>
                        ) : null}
                      </div>
                    ),
                  )}
                </div>
                {filterDate ? (
                  <div className="md-cal-filter">
                    Showing {filterDate} ·{' '}
                    <button type="button" onClick={() => setFilterDate(null)}>
                      show all
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="md-right">
            {!shown.length ? (
              <div className="md-empty">
                No decks{filterDate ? ` on ${filterDate}` : ' yet'}.
              </div>
            ) : (
              shown.map((d) => (
                <div className="md-deck" key={d.id}>
                  <div className="md-deck-row">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="md-deck-t">{d.title}</div>
                      <div className="md-deck-m">
                        <span
                          className={`md-badge ${d.source === 'generated' ? 'gen' : 'up'}`}
                        >
                          {d.source === 'generated' ? 'AI-generated' : 'Uploaded'}
                        </span>{' '}
                        · {d.meeting_date ?? 'no date'}
                        {d.delivery_project_id ? ` · ${d.delivery_project_id}` : ''}
                        {d.slide_count ? ` · ${d.slide_count} slides` : ''}
                        {d.bytes ? ` · ${Math.round(d.bytes / 1024)} KB` : ''}
                      </div>
                    </div>
                    <a
                      className="md-dl"
                      href={`/api/project_management/meeting-deck/${d.id}/download`}
                    >
                      <i className="material-icons">file_download</i>
                    </a>
                    <button
                      type="button"
                      className="md-del"
                      title="Delete"
                      onClick={() => void removeDeck(d.id)}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export const WeeklyStatusMeetings = () => <MeetingDecks slug="weekly-status" />;
export const SteercoDeckCreation = () => <MeetingDecks slug="steerco-deck" />;
